import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Theme Context
 *
 * Provides theme switching functionality (light/dark mode).
 * Persists preference to localStorage and respects system preference.
 *
 * Usage:
 * const { theme, setTheme, toggleTheme } = useTheme();
 */

const ThemeContext = createContext({
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
  systemTheme: 'dark',
});

// Storage key for theme preference
const THEME_STORAGE_KEY = 'mervo-theme';

// Get system color scheme preference
const getSystemTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

// Get stored theme preference or default
const getStoredTheme = () => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const ThemeProvider = ({ children, defaultTheme = 'system' }) => {
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const [themePreference, setThemePreference] = useState(() => {
    return getStoredTheme() || defaultTheme;
  });

  // Resolved theme (actual light/dark value)
  const theme = themePreference === 'system' ? systemTheme : themePreference;

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');

    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'light' : 'dark');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(theme);

    // Also set data attribute for more specific targeting
    root.setAttribute('data-theme', theme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'dark' ? '#1A1A1A' : '#FFFFFF');
    }
  }, [theme]);

  // Save preference to localStorage
  const setTheme = useCallback((newTheme) => {
    setThemePreference(newTheme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (e) {
      console.warn('Could not save theme preference:', e);
    }
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  }, [theme, setTheme]);

  const value = {
    theme, // Resolved theme ('light' or 'dark')
    themePreference, // User preference ('light', 'dark', or 'system')
    systemTheme, // System preference
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
